import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";

// Compute current minute-of-day in Eastern Time (matches backend authoritative clock)
function nowEtMinutes() {
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/New_York",
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
        }).formatToParts(new Date());
        let h = 0, m = 0;
        for (const p of parts) {
            if (p.type === "hour") h = parseInt(p.value, 10);
            if (p.type === "minute") m = parseInt(p.value, 10);
        }
        return h * 60 + m;
    } catch {
        const d = new Date();
        return d.getHours() * 60 + d.getMinutes();
    }
}

function formatDelta(deltaMin) {
    if (deltaMin <= 0 && deltaMin > -1) return { label: "now", tone: "due" };
    if (deltaMin < 0) {
        const a = Math.abs(deltaMin);
        if (a < 60) return { label: `${a}m ago`, tone: "past" };
        const h = Math.floor(a / 60);
        const m = a % 60;
        return { label: `${h}h${m ? ` ${m}m` : ""} ago`, tone: "past" };
    }
    if (deltaMin < 1) return { label: "now", tone: "due" };
    if (deltaMin < 60) return { label: `in ${deltaMin}m`, tone: deltaMin <= 5 ? "soon" : "future" };
    const h = Math.floor(deltaMin / 60);
    const m = deltaMin % 60;
    return { label: `in ${h}h${m ? ` ${m}m` : ""}`, tone: "future" };
}

const TONE_STYLES = {
    due:    "bg-terracotta text-cream",
    soon:   "bg-mustard text-warm-900",
    future: "bg-sage-soft text-warm-900",
    past:   "bg-surface text-warm-500",
};

/** Live countdown chip for a reminder time (HH:MM in Eastern Time). */
export default function Countdown({ time, completed = false }) {
    const [now, setNow] = useState(nowEtMinutes());

    useEffect(() => {
        if (completed) return;
        const tick = () => setNow(nowEtMinutes());
        tick();
        const id = setInterval(tick, 20_000); // refresh every 20s
        return () => clearInterval(id);
    }, [completed]);

    if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
    if (completed) return null;

    const [h, m] = time.split(":").map((x) => parseInt(x, 10));
    const target = h * 60 + m;
    const delta = target - now;
    const { label, tone } = formatDelta(delta);

    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full ${TONE_STYLES[tone]}`}
            data-testid="countdown-chip"
            title={`Reminder time: ${time} ET`}
        >
            <Clock size={10} />
            {label}
        </span>
    );
}
